export const PRINT_QUEUE_VERSION = 1;
/** After QZ accepts a spool, wait this long then auto-clear the finished bundle. */
export const PRINT_QUEUE_AUTO_CLEAR_MS = 30000;
export const PRINT_QUEUE_ITEM_STATES = Object.freeze({
  STAGED: 'staged',
  VALIDATING: 'validating',
  GENERATING: 'generating',
  SUBMITTING: 'submitting',
  ACCEPTED: 'queue-accepted',
  SETTLING: 'settling',
  PAUSED: 'paused',
  UNCERTAIN: 'uncertain',
  AWAITING_CONFIRMATION: 'awaiting-physical-confirmation',
  CONFIRMED: 'confirmed',
  SKIPPED: 'skipped',
  CANCELED: 'canceled',
});

const id = (prefix) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

export function toPersistablePrintSnapshot(value, seen = new WeakSet()) {
  if (value == null || ['string', 'number', 'boolean'].includes(typeof value)) return value;
  if (typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => toPersistablePrintSnapshot(entry, seen))
      .filter((entry) => entry !== undefined);
  }
  if (typeof value === 'object') {
    if (seen.has(value)) return undefined;
    seen.add(value);
    const output = {};
    Object.entries(value).forEach(([key, entry]) => {
      const next = toPersistablePrintSnapshot(entry, seen);
      if (next !== undefined) output[key] = next;
    });
    seen.delete(value);
    return output;
  }
  return String(value);
}

export const getQueueOrderKey = (order) =>
  String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderId ||
      order?.order_id ||
      order?.id ||
      '',
  )
    .replace(/^#/, '')
    .trim();

export function createEmptyPrintQueue() {
  return {
    version: PRINT_QUEUE_VERSION,
    bundles: [],
    running: false,
    pauseRequested: false,
    activeItemId: null,
    blockedItemId: null,
    settleDelayMs: 1200,
    updatedAt: new Date().toISOString(),
  };
}

export function createPrintQueueItem(input) {
  return {
    id: input.id || id('print-item'),
    kind: input.kind,
    profileKey: input.profileKey,
    label: String(input.label || input.kind || 'Print item'),
    recipe: input.recipe || {},
    routing: input.routing || {},
    status: PRINT_QUEUE_ITEM_STATES.STAGED,
    attempts: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function createPrintQueueBundle(order, items = [], input = {}) {
  const orderKey = getQueueOrderKey(order);
  return {
    id: input.id || id('print-bundle'),
    orderKey,
    label: String(input.label || `Order ${orderKey || 'unassigned'}`),
    orderSnapshot: order ? toPersistablePrintSnapshot(order) : null,
    status: 'staged',
    items: items.map((item) => createPrintQueueItem(item)),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

const isMergeableBundle = (bundle) =>
  bundle.status === 'staged' &&
  bundle.items.every((item) =>
    [PRINT_QUEUE_ITEM_STATES.STAGED, PRINT_QUEUE_ITEM_STATES.PAUSED].includes(item.status),
  );

export function addOrMergePrintBundle(state, incoming) {
  const bundles = [...state.bundles];
  const existingIndex = bundles.findIndex(
    (bundle) =>
      incoming.orderKey && bundle.orderKey === incoming.orderKey && isMergeableBundle(bundle),
  );
  if (existingIndex < 0) {
    bundles.push(incoming);
  } else {
    const existing = bundles[existingIndex];
    const incomingByKind = new Map(incoming.items.map((item) => [item.kind, item]));
    const mergedItems = existing.items.map((item) => {
      const replacement = incomingByKind.get(item.kind);
      if (!replacement) return item;
      incomingByKind.delete(item.kind);
      return { ...replacement, id: item.id };
    });
    bundles[existingIndex] = {
      ...existing,
      orderSnapshot: incoming.orderSnapshot || existing.orderSnapshot,
      items: [...mergedItems, ...incomingByKind.values()],
      updatedAt: new Date().toISOString(),
    };
  }
  return { ...state, bundles, updatedAt: new Date().toISOString() };
}

export function prependPrintBundle(state, incoming) {
  return {
    ...state,
    bundles: [incoming, ...(state.bundles || [])],
    updatedAt: new Date().toISOString(),
  };
}

export function moveQueueEntry(list, fromIndex, toIndex) {
  if (
    !Array.isArray(list) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [entry] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, entry);
  return next;
}

export function updateQueueItem(state, itemId, patch) {
  return {
    ...state,
    bundles: state.bundles.map((bundle) => ({
      ...bundle,
      items: bundle.items.map((item) =>
        item.id === itemId
          ? { ...item, ...patch, updatedAt: new Date().toISOString() }
          : item,
      ),
    })),
    updatedAt: new Date().toISOString(),
  };
}

export function getOrderedQueueItems(state) {
  return state.bundles.flatMap((bundle, bundleIndex) =>
    bundle.items.map((item, itemIndex) => ({
      ...item,
      bundleId: bundle.id,
      orderKey: bundle.orderKey,
      orderSnapshot: bundle.orderSnapshot,
      bundleIndex,
      itemIndex,
    })),
  );
}

export function getPrintBundlePaperAvailability(bundle) {
  const availableKinds = new Set(
    (bundle?.items || [])
      .filter(
        (item) =>
          ![
            PRINT_QUEUE_ITEM_STATES.SKIPPED,
            PRINT_QUEUE_ITEM_STATES.CANCELED,
          ].includes(item.status),
      )
      .map((item) => item.kind),
  );
  return {
    pickList: availableKinds.has('pick-list'),
    packingSlip:
      availableKinds.has('packing-slip') || availableKinds.has('cheetah-label'),
    internalLabel:
      availableKinds.has('cheetah-label') ||
      availableKinds.has('go4-outside-label'),
    inscription: availableKinds.has('gift-card'),
    carrierLabel: availableKinds.has('carrier-pdf'),
    separator: availableKinds.has('separator'),
  };
}

export function restorePrintQueueState(value) {
  const state = value?.version === PRINT_QUEUE_VERSION ? value : createEmptyPrintQueue();
  let blockedItemId = state.blockedItemId || null;
  const interrupted = new Set([
    PRINT_QUEUE_ITEM_STATES.VALIDATING,
    PRINT_QUEUE_ITEM_STATES.GENERATING,
    PRINT_QUEUE_ITEM_STATES.SUBMITTING,
    PRINT_QUEUE_ITEM_STATES.SETTLING,
  ]);
  const bundles = (state.bundles || []).map((bundle) => ({
    ...bundle,
    items: (bundle.items || []).map((item) => {
      if (!interrupted.has(item.status)) return item;
      blockedItemId = item.id;
      return {
        ...item,
        status: PRINT_QUEUE_ITEM_STATES.UNCERTAIN,
        error: 'The browser closed while this item was active. Check physical output before continuing.',
      };
    }),
  }));
  return {
    ...createEmptyPrintQueue(),
    ...state,
    bundles,
    running: false,
    pauseRequested: false,
    activeItemId: null,
    blockedItemId,
  };
}

export function removeQueueBundle(state, bundleId) {
  return {
    ...state,
    bundles: state.bundles.filter((bundle) => bundle.id !== bundleId),
    updatedAt: new Date().toISOString(),
  };
}
